import { effectiveOntimeFlag } from './complaint-ontime';

export type ClosureStatus =
  | 'overdueReply'
  | 'awaitingReply'
  | 'defectPending'
  | 'temp'
  | 'verifying'
  | 'closed';

const CLOSURE_STATUS_META: Record<
  ClosureStatus,
  { label: string; tag: 'danger' | 'warning' | 'primary' | 'success' }
> = {
  overdueReply: { label: '超期未回复', tag: 'danger' },
  awaitingReply: { label: '待回复', tag: 'warning' },
  defectPending: { label: '不良品待处理', tag: 'warning' },
  temp: { label: '已回复对策（临时）', tag: 'warning' },
  verifying: { label: '永久对策实施/验证中', tag: 'primary' },
  closed: { label: '已结案', tag: 'success' },
};

/** 与投诉明细表格/详情抽屉中的闭环状态文案一致 */
export const CLOSURE_STATUS_LABEL: Record<ClosureStatus, string> = {
  overdueReply: CLOSURE_STATUS_META.overdueReply.label,
  awaitingReply: CLOSURE_STATUS_META.awaitingReply.label,
  defectPending: CLOSURE_STATUS_META.defectPending.label,
  temp: CLOSURE_STATUS_META.temp.label,
  verifying: CLOSURE_STATUS_META.verifying.label,
  closed: CLOSURE_STATUS_META.closed.label,
};

/** 筛选下拉顺序 */
export const CLOSURE_FILTER_OPTIONS: { value: ClosureStatus; label: string }[] = [
  { value: 'overdueReply', label: CLOSURE_STATUS_META.overdueReply.label },
  { value: 'awaitingReply', label: CLOSURE_STATUS_META.awaitingReply.label },
  { value: 'defectPending', label: CLOSURE_STATUS_META.defectPending.label },
  { value: 'temp', label: CLOSURE_STATUS_META.temp.label },
  { value: 'verifying', label: CLOSURE_STATUS_META.verifying.label },
  { value: 'closed', label: CLOSURE_STATUS_META.closed.label },
];

export interface ClosureInput {
  permanent?: string | null;
  replyPlan?: string | null;
  replyActual?: string | null;
  replyOntime?: string | null;
  defectActual?: string | null;
  closeActual?: string | null;
}

export interface Rate {
  filled: number;
  total: number;
  percent: number | null;
}

function isFilled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** 永久对策：空白和单独的 * 算没填。日期格的 * 不走这里。 */
function permanentFilled(value: string | null | undefined): boolean {
  if (!isFilled(value)) return false;
  return (value as string).trim() !== '*';
}

function planYmd(value: string | null | undefined): string | null {
  if (!value) return null;
  const matched = /^\d{4}-\d{2}-\d{2}/.exec(String(value).trim());
  return matched ? matched[0] : null;
}

function dateFilled(value: string | null | undefined): boolean {
  return planYmd(value) != null || (typeof value === 'string' && value.trim() === '*');
}

export function closureStatusOf(row: ClosureInput, today: string): ClosureStatus {
  if (isFilled(row.closeActual)) return 'closed';
  if (permanentFilled(row.permanent)) return 'verifying';
  if (isFilled(row.replyActual)) return 'temp';
  const plan = planYmd(row.replyPlan);
  if (plan && plan < today) return 'overdueReply';
  if (dateFilled(row.defectActual)) return 'awaitingReply';
  return 'defectPending';
}

/** 超期未回复的天数；其他状态返回 null。 */
export function overdueDays(row: ClosureInput, today: string): number | null {
  if (closureStatusOf(row, today) !== 'overdueReply') return null;
  const plan = planYmd(row.replyPlan) as string;
  return Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${plan}T00:00:00Z`)) / 86400000,
  );
}

export function closureLabel(row: ClosureInput, today: string): string {
  const status = closureStatusOf(row, today);
  const name = CLOSURE_STATUS_META[status].label;
  if (status !== 'overdueReply') return name;
  const days = overdueDays(row, today);
  if (days == null) return name;
  return `${name} · ${days} 天`;
}

/** 异常回复及时：分子是已回复且「是否按时」为「是」，分母是已回复行。 */
export function replyOntimeRate(rows: ClosureInput[]): Rate {
  const replied = rows.filter((row) => isFilled(row.replyActual));
  const ontime = replied.filter((row) =>
    effectiveOntimeFlag(row.replyPlan, row.replyActual, row.replyOntime),
  );
  if (replied.length === 0) return { filled: 0, total: 0, percent: null };
  return {
    filled: ontime.length,
    total: replied.length,
    percent: Math.round((ontime.length / replied.length) * 1000) / 10,
  };
}
